/**
 * When the phone reads `/v1/me` again on its own, so the role it draws doors
 * by is not the one it had at launch (step 6.3 of docs/plans/roles-parity.md).
 *
 * Two kinds of re-read. Coming back to the app re-reads at most once a minute
 * (`dueOnActive`): somebody may have changed this person's role on another
 * device, and a minute is as stale as a door may be. A write of the phone's
 * own that can move the caller's standing (a role saved, a role given to
 * yourself, a hand-over offered or accepted) re-reads at once, whatever the
 * clock says.
 *
 * Pure, so `me-refresh.test.ts` holds it without a device.
 */
import type { MeResponse } from '@alluzo/shared';

/** At most one re-read on coming back to the app in this long. */
export const ME_REFRESH_MS = 60_000;

/** Whether coming back to the app re-reads: never read yet, or the last read a minute old. */
export const dueOnActive = (lastReadAt: number | null, now: number): boolean =>
  lastReadAt === null || now - lastReadAt >= ME_REFRESH_MS;

/**
 * Whether a re-read answered something new. The same answer keeps the held
 * one, so every screen reading `/v1/me` is not drawn again for nothing.
 */
export const meChanged = (held: MeResponse | null, next: MeResponse): boolean =>
  held === null || JSON.stringify(held) !== JSON.stringify(next);

/**
 * Whether a change to one person moves the caller's own standing: a role
 * given to themselves. Their Reports to or their contact moves no door.
 */
export const movesOwnStanding = (
  userId: string,
  request: { readonly roleId?: unknown },
  myUserId: string | null,
): boolean => myUserId !== null && userId === myUserId && request.roleId !== undefined;
